import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { sitePath } from "../../lib/siteRouting";
import publicApi from "../../api/public";
import SiteLayout from "../../components/site/SiteLayout";
import { ArrowRight, MapPin } from "lucide-react";
import SEOHead from "../../components/site/SEOHead";
import { SmartImage } from "../../components/site/SmartImage";
import { SkeletonGrid } from "../../components/site/SkeletonCard";
import { motion } from "framer-motion";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function SiteProjets() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    publicApi
      .get("/projects")
      .then(({ data }) => setItems(data))
      .finally(() => setLoading(false));
  }, []);

  return (
    <SiteLayout>
      <SEOHead
        title="Nos projets immobiliers"
        description="Découvrez les résidences haut standing et lots de villas TPCO Sénégal à Dakar : Projet Taif, TPCO Gestion et autres réalisations immobilières."
      />

      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-20 pb-10">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#E91E63] font-bold mb-4">// Nos réalisations</div>
        <h1
          className="font-display text-5xl lg:text-7xl font-black leading-[0.95] tracking-tight mb-6 text-[#0F1A3D]"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          Projets immobiliers
        </h1>
        <p className="text-[#4B5775] text-lg max-w-2xl leading-relaxed">
          Découvrez nos résidences d'exception et lots de maisons individuelles à Dakar et au Sénégal —
          chaque projet incarne notre exigence de qualité et notre savoir-faire en construction haut de gamme.
        </p>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-24">
        {loading ? (
          <SkeletonGrid count={6} />
        ) : items.length === 0 ? (
          <div className="py-32 text-center text-[#9CA3B5] border-y border-[#E2E5F1]">
            Aucun projet publié pour le moment.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="site-projects-grid">
            {items.map((p, idx) => (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5, delay: Math.min(idx * 0.06, 0.4), ease: [0.16, 1, 0.3, 1] }}
              >
                <Link
                  to={sitePath(`/projet/${p.slug || p.id}`)}
                  data-testid={`site-project-card-${p.id}`}
                  className="group block bg-white border border-[#E2E5F1] hover-lift overflow-hidden h-full"
                >
                  <div className="overflow-hidden">
                    <div className="img-zoom-hover">
                      <SmartImage
                        src={p.cover_url ? `${BACKEND}${p.cover_url}` : null}
                        alt={p.nom}
                        aspectRatio="aspect-[4/3]"
                        fallbackLabel={p.type === "maison" ? "Lot maisons" : "Résidence"}
                      />
                    </div>
                  </div>

                  <div className="p-7 md:p-8 flex flex-col gap-3">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-[#E91E63] font-bold">
                      {p.type === "maison" ? "Lot maisons" : "Immeuble"}
                    </div>
                    <h3
                      className="font-display text-2xl lg:text-[26px] font-bold leading-[1.15] tracking-tight text-[#0F1A3D] group-hover:text-[#1E3A8A] transition-colors"
                      style={{ fontFamily: "'Playfair Display', serif" }}
                    >
                      {p.nom}
                    </h3>
                    {p.slogan && (
                      <p className="text-sm text-[#4B5775] line-clamp-2 leading-relaxed">{p.slogan}</p>
                    )}
                    <div className="flex items-center gap-2 text-xs text-[#4B5775] mt-1">
                      <MapPin className="w-3.5 h-3.5 text-[#1E3A8A]" /> {p.quartier || p.adresse}, {p.ville}
                    </div>
                    <div className="flex items-center justify-between text-xs mt-4 pt-5 border-t border-[#E2E5F1]">
                      <span className="text-[#4B5775] font-medium">
                        {p.nb_unites} unité{p.nb_unites > 1 ? "s" : ""} · {p.nb_disponibles} disponible
                        {p.nb_disponibles > 1 ? "s" : ""}
                      </span>
                      <span className="text-[#1E3A8A] font-bold uppercase tracking-wider inline-flex items-center gap-1.5 group-hover:gap-3 transition-all duration-300">
                        Découvrir <ArrowRight className="w-3.5 h-3.5" />
                      </span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </section>
    </SiteLayout>
  );
}
